"use client";
import React, {
  forwardRef,
  ComponentProps,
  ReactNode,
  ButtonHTMLAttributes,
} from "react";
import { MouseEventHandler } from "react";
import styles from "./Button.module.scss";
import { useRouter } from "next/compat/router";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { customSanitize } from "@/utils/customSanitize.ts";
import { removeExtraSpacesFromClassNames } from "@/utils/removeExtraSpacesFromClassNames";
import { CustomLink } from "../CustomLink";
import { FadeInFromBottom, FadeInWithPulse } from "../Animation/Animation";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG";
import animationStyles from "./ButtonAnimation.module.scss";

const PrimaryArrowIcon = () => {
  const { getIcon } = useGlobalContext();
  return (
    <CustomSVG
      src={getIcon("altArrowRight")}
      className={`is-flex ${animationStyles.icon}`}
      wrapper={"span"}
      width={12}
      height={12}
      aria-hidden={true}
    />
  );
};

export type ButtonTypes = "primary" | "secondary";

const getButtonClasses = ({
  type,
  invert = false,
  isBack = false,
  isDownload = false,
  className = "",
}: {
  type: ButtonTypes;
  invert?: boolean;
  isBack?: boolean;
  isDownload?: boolean;
  className?: string;
}) => {
  return removeExtraSpacesFromClassNames(
    `btn ${styles.btn} ${styles[`is-${type}`]} ${
      isDownload
        ? animationStyles["download-slide-animation"]
        : type === "secondary"
          ? animationStyles["slide-animation"]
          : animationStyles["primary-slide-animation"]
    } ${invert ? animationStyles["invert"] : ""} ${className || ""} ${
      isBack ? styles["is-back"] : ""
    }`,
  );
};

interface ButtonProps extends ComponentProps<"a"> {
  type: ButtonTypes;
  children: ReactNode;
  animation?: "fade" | "pulse" | "none";
  /** Renders normal button component with on Click Event Handler*/
  onClick?: MouseEventHandler<HTMLElement>;
  href: string;
  className?: string;
  target: string;
  /**
   * Creates a link with no prefetching abilities... which Next Links per default have
   */
  disablePrefetch?: boolean;
  /**
   * Creates a link which triggers prefetching only on hover... Next Links prefetch per default as soon as link is visible
   */
  onlyPrefetchOnHover?: boolean;
  isDownload?: boolean;
  isBack?: boolean;
  invert?: boolean;
}

export const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      type,
      children,
      animation,
      onClick,
      href = "_self",
      className,
      target = "_self",
      disablePrefetch = false,
      onlyPrefetchOnHover = false,
      isDownload = false,
      isBack = false,
      invert = false,
      ...props
    }: ButtonProps,
    ref,
  ) => {
    const { asPath } = useRouter() ?? {};
    const { getIcon } = useGlobalContext();

    const btnClassName = getButtonClasses({
      type,
      invert,
      isBack,
      isDownload,
      className,
    });

    if (!href && !onClick) {
      throw new Error(`No href provided at "Button.tsx" at ${asPath}`);
    }

    const handleClick = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      onClick?.(e);
    };

    const renderContent = () => (
      <>
        {type === "secondary" && isDownload && (
          <CustomSVG
            src={getIcon("download")}
            className={`is-flex ${animationStyles.icon}`}
            wrapper={"span"}
            width={32}
            height={32}
            aria-hidden={true}
          />
        )}
        {type === "secondary" && !isDownload && <PrimaryArrowIcon />}

        {type === "primary" && isDownload && (
          <CustomSVG
            src={getIcon("download")}
            className={`is-flex ${animationStyles.icon}`}
            wrapper={"span"}
            width={32}
            height={32}
            aria-hidden={true}
          />
        )}
        {type === "primary" && !isDownload && <PrimaryArrowIcon />}
        {children}
      </>
    );

    const innerButtonJSX =
      !!href && !onClick ? (
        <CustomLink
          href={href}
          target={target}
          className={btnClassName}
          disablePrefetch={disablePrefetch}
          onlyPrefetchOnHover={onlyPrefetchOnHover}
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...props}
        >
          {renderContent()}
        </CustomLink>
      ) : (
        onClick && (
          <button
            onClick={handleClick}
            className={`${btnClassName} button-reset`}
            aria-label={props["aria-label"] ?? ""}
            ref={ref as React.Ref<HTMLButtonElement>}
            {...(props as ComponentProps<"button">)}
          >
            {renderContent()}
          </button>
        )
      );

    if (!innerButtonJSX) {
      return null;
    }

    return animation === "fade" ? (
      <FadeInFromBottom>{innerButtonJSX}</FadeInFromBottom>
    ) : animation === "pulse" ? (
      <FadeInWithPulse>{innerButtonJSX}</FadeInWithPulse>
    ) : (
      innerButtonJSX
    );
  },
);
Button.displayName = "Button";

interface SimpleButtonProp extends ButtonHTMLAttributes<HTMLButtonElement> {
  text: string;
  extraClassName?: string;
  btnType?: ButtonTypes;
}

export const SimpleButton = forwardRef<HTMLButtonElement, SimpleButtonProp>(
  ({ text, extraClassName, btnType = "primary", ...props }, ref) => {
    const btnClassName = getButtonClasses({
      type: btnType,
      className: extraClassName,
    });

    return (
      <button className={btnClassName + " button-reset"} ref={ref} {...props}>
        <span dangerouslySetInnerHTML={{ __html: customSanitize(text) }}></span>
        {btnType === "secondary" && <PrimaryArrowIcon />}
        {btnType === "primary" && <PrimaryArrowIcon />}
      </button>
    );
  },
);

SimpleButton.displayName = "SimpleButton";
